/**
 * dexScrollWindow — scroll-position plausibility gate for live dex OCR.
 *
 * WHY THIS EXISTS (2026-07-11, Lucky #039/#041/#044 false writes).
 *   On a scroll/motion frame Apple Vision occasionally drops a digit from the
 *   4-digit dex number — e.g. `0739 → 039`, `0741 → 041`, `0744 → 044`. The
 *   sprite is a genuine catch, but the mangled NUMBER re-attributes it to a
 *   wildly different species (Alola #739 Golisopod → Kanto #39 Jigglypuff), and
 *   because the mis-read is STABLE across frames it survives the 2-frame
 *   confirmation gate and writes. The region cross-check can't catch it either:
 *   it trusts the frame's own region, which the same mis-reads corrupt.
 *
 * THE INVARIANT WE ENFORCE.
 *   The on-screen dex position moves CONTINUOUSLY as you scroll, so a frame's
 *   numbers must sit near where you've been reading — the running scroll ANCHOR.
 *   A number far BELOW that anchor (39 while you're at ~750) is a mangled read.
 *   We only ever reject BACKWARD outliers: forward / high numbers always pass, so
 *   sparse dexes (shadow/purified/mega/gmax), whose visible numbers are gapped
 *   and can be much higher, are never throttled. This is variant-agnostic and
 *   makes NO assumption of a consecutive grid.
 *
 * Pure + hermetic (no session state) so it unit-tests directly. The session owns
 * the anchor and feeds it in / stores the returned `nextAnchor`.
 */

/**
 * How far BELOW the scroll anchor a number may sit before it is treated as a
 * backward outlier (a digit-drop / region-jump mis-read). Deliberately GENEROUS:
 * a dropped hundreds digit is a ~600–700 jump (7xx → xx) and a region mis-read is
 * hundreds apart, whereas even a hard up-flick only scrolls a page or two (tens)
 * per frame. 200 sits comfortably in that gap — it catches every digit-drop while
 * never vetoing a legitimate backward scroll (and a full-page jump past the window
 * re-anchors instead of vetoing).
 */
export const SCROLL_BACK_WINDOW = 200;

export interface ScrollWindowResult {
  /** Per-input-number: true = plausible (keep), false = backward outlier (veto). */
  keep: boolean[];
  /** How many numbers were vetoed as backward outliers. */
  vetoedCount: number;
  /**
   * The anchor to carry into the next frame. Advances to the lead (min) of the
   * KEPT numbers so vetoed outliers never drag it down; on a genuine fast
   * backward scroll (whole frame below the window) it re-anchors to the frame.
   */
  nextAnchor: number | null;
  /** True when the whole frame fell below the window → treated as a fast scroll, not vetoed. */
  reAnchored: boolean;
}

function minOf(nums: number[]): number {
  let m = nums[0];
  for (const n of nums) if (n < m) m = n;
  return m;
}

/**
 * Flag backward-outlier dex numbers relative to the running scroll anchor.
 *
 * @param numbers  the frame's read dex numbers (any order).
 * @param anchor   the running scroll anchor (lead of the last plausible frame), or null to bootstrap.
 * @param backWindow  override the default {@link SCROLL_BACK_WINDOW}.
 */
export function filterScrollBackwardOutliers(
  numbers: readonly number[],
  anchor: number | null,
  backWindow: number = SCROLL_BACK_WINDOW,
): ScrollWindowResult {
  if (numbers.length === 0) {
    return { keep: [], vetoedCount: 0, nextAnchor: anchor, reAnchored: false };
  }
  const arr = numbers as number[];
  // Bootstrap: no history yet, so we cannot judge plausibility — keep all and
  // seed the anchor from this frame's lead. The downstream 2-frame confirmation
  // gate still protects against a single bad first frame.
  if (anchor == null) {
    return { keep: arr.map(() => true), vetoedCount: 0, nextAnchor: minOf(arr), reAnchored: false };
  }
  const floor = anchor - backWindow;
  const anyAtOrAboveFloor = arr.some((n) => n >= floor);
  if (!anyAtOrAboveFloor) {
    // Every number is far below the anchor: a genuine fast backward scroll (or a
    // fully-mangled frame the confirmation gate will reject on its own). Trust
    // the frame, re-anchor to it, and veto nothing.
    return { keep: arr.map(() => true), vetoedCount: 0, nextAnchor: minOf(arr), reAnchored: true };
  }
  // The anchor is still valid for this frame (at least one number is near/above
  // it). Veto only the numbers that sit far BELOW the floor — the backward
  // outliers. Forward/high numbers always pass (sparse-dex safe).
  const keep = arr.map((n) => n >= floor);
  const kept = arr.filter((_, i) => keep[i]);
  const vetoedCount = arr.length - kept.length;
  const nextAnchor = kept.length > 0 ? minOf(kept) : anchor;
  return { keep, vetoedCount, nextAnchor, reAnchored: false };
}
